import { computed, ref, type Ref, watch } from 'vue';
import type { Insight, InsightAxis, InsightAxisType } from '@/types/insights';
import { useSwrPolling } from '@/utils/composables/useSwrPolling';
import {
  hasProcessingAxis,
  insightPayloadAxes,
} from '@/utils/insights/insightAxis';
import { useInsightSources } from '@/utils/swr';

export const INSIGHT_SOURCES_POLLING_INTERVAL_MS = 2000;

const isGeneratingSources = (axis: InsightAxis) =>
  axis.generation_status === 'processed' &&
  axis.source_generation_status === 'processing';

export const useInsightSourcesPolling = (insight: Ref<Insight | undefined>) => {
  const axesOfSnapshot = (predicate: (axis: InsightAxis) => boolean) => {
    const payload = insight.value?.payload;
    if (payload === undefined) return [];
    return insightPayloadAxes(payload)
      .filter(predicate)
      .map(axis => axis.axis);
  };

  const snapshotProcessingAxes = computed(() =>
    axesOfSnapshot(isGeneratingSources),
  );
  const snapshotHasProcessing = computed(
    () => snapshotProcessingAxes.value.length > 0,
  );

  const enabled = ref(false);
  const { data, mutate } = useInsightSources(enabled);

  const isCurrentGeneration = computed(
    () =>
      data.value !== undefined &&
      data.value.insight_uuid !== null &&
      data.value.insight_uuid === insight.value?.uuid,
  );

  const publishedAxesAtFetch = ref<InsightAxisType[]>([]);
  const fetchSources = async () => {
    const published = axesOfSnapshot(
      axis => axis.generation_status === 'processed',
    );
    await mutate();
    publishedAxesAtFetch.value = published;
  };

  const responseHasProcessing = computed(
    () =>
      data.value?.axes.some(
        axis => axis.source_generation_status === 'processing',
      ) ?? false,
  );

  /**
   * 未公開の軸もレスポンスには processed（中身は空）で載るため、ステータスだけでは解決済みか判断できない。
   * 取得を始めた時点で公開されていなかった軸は、そのレスポンスが公開より前のものなので未解決として扱う。
   */
  const unresolvedInResponse = computed(() =>
    snapshotProcessingAxes.value.some(axisType => {
      if (!publishedAxesAtFetch.value.includes(axisType)) return true;
      const status = data.value?.axes.find(
        axis => axis.axis === axisType,
      )?.source_generation_status;
      return status !== 'processed' && status !== 'error';
    }),
  );

  const snapshotWaitsForSources = computed(
    () =>
      snapshotHasProcessing.value &&
      (data.value === undefined ||
        !isCurrentGeneration.value ||
        responseHasProcessing.value),
  );

  const shouldPoll = computed(() => {
    // 遅延到着する軸が出典生成中のまま公開されることがあるので、生成中の軸が残る間は止めない。
    const hasUnsettledAxis = hasProcessingAxis(insight.value?.payload);
    return (
      enabled.value &&
      (hasUnsettledAxis ||
        snapshotWaitsForSources.value ||
        (snapshotHasProcessing.value && unresolvedInResponse.value))
    );
  });

  watch(
    snapshotHasProcessing,
    hasProcessing => {
      if (hasProcessing && !enabled.value) {
        enabled.value = true;
      }
    },
    { immediate: true },
  );

  useSwrPolling(fetchSources, shouldPoll, INSIGHT_SOURCES_POLLING_INTERVAL_MS);

  // 公開前に取得したレスポンスは未公開だった軸を空で含むため、その軸に適用するとスナップショットの出典を消してしまう。
  // 取得を始めた時点で公開済みだった軸だけを返し、それ以外はスナップショットの出典に任せる。
  const sources = computed(() => {
    if (!isCurrentGeneration.value || data.value === undefined)
      return undefined;
    return data.value.axes.filter(axis =>
      publishedAxesAtFetch.value.includes(axis.axis),
    );
  });

  return { sources };
};
